import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// React shared.

// Hooks.
import useToggleMenu from 'app/client/utilities/hooks/useToggleMenu';

// App components.
import ModalEscape from 'app/client/components/ModalEscape';
import Alert from 'app/client/components/Alert';

// HOC and store.
import ErrorBoundary from 'app/client/components/Sections/ErrorBoundary';
import boost from 'app/client/utilities/hoc/boost';
import fetcher from './fetcher';
import store from './store';

// Layout components.
import BootLoader from './components/BootLoader';
import Menu from './components/Menu';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Styled.
import * as S from './styled';

const Layout = ({ authorized, customer, newVersion, error = null, clearError, children }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { ref, buttonRef, open, handleToggle } = useToggleMenu();

  // The error belongs to the page that failed to load, so clear it when another page is opened.
  useEffect(() => clearError, [pathname, clearError]);

  return (
    <>
      <ModalEscape />
      <Helmet defaultTitle={t('core:appTitle')} titleTemplate={`%s - ${t('core:appTitle')}`} />
      <S.ButtonWrapper>
        <S.MenuButton ref={buttonRef} onClick={handleToggle} open={open} authorized={authorized}>
          <div>
            <span />
            <span />
            <span />
          </div>
        </S.MenuButton>
      </S.ButtonWrapper>
      {authorized && <Menu ref={ref} customer={customer} open={open} />}
      <S.Container authorized={authorized}>
        <Navbar authorized={authorized} open={open} />
        <S.Content>
          {error && (
            <Alert
              error
              title={t(`${error}.title`)}
              message={t(`${error}.message`)}
              onDismiss={clearError}
            />
          )}
          {newVersion && (
            <Alert
              info
              title={t('core:messages.newVersion.title')}
              message={
                <S.NewVersionLink onClick={() => window.location.reload()}>
                  {t('core:messages.newVersion.message')}
                </S.NewVersionLink>
              }
            />
          )}
          <ErrorBoundary customer={customer}>{children}</ErrorBoundary>
        </S.Content>
        <Footer />
      </S.Container>
    </>
  );
};

Layout.propTypes = {
  // Passed as props.
  authorized: PropTypes.bool.isRequired,
  customer: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,

  // From store.
  newVersion: PropTypes.bool.isRequired,
  error: PropTypes.string,
  clearError: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
};

export default boost({ fetcher, store, loader: BootLoader })(Layout);
